import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "./motion";
import { useI18n } from "@/i18n";

const VOLUMES = ["$1,000 - $5,000 USD", "$5,000 - $20,000 USD", "+$20,000 USD"];
const fieldOrder = ["company_name", "tax_id", "email", "phone"];
const fieldTypes = { company_name: "text", tax_id: "text", email: "email", phone: "tel" };

export function RegisterForm() {
  const { t } = useI18n();
  const empty = { company_name: "", tax_id: "", email: "", phone: "", volume: VOLUMES[0] };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success(t.form.toastSuccess);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.form.toastError);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-[#0a0a0a] border border-white/10 rounded-none px-4 py-3.5 text-sm text-[#f5f5f5] placeholder:text-[#555] font-plex focus:outline-none focus:border-[#ff4500] focus:ring-1 focus:ring-[#ff4500] transition-colors";

  return (
    <section id="registro" className="relative bg-[#050505] py-24 md:py-32 grid-lines">
      <div className="w-full max-w-5xl mx-auto px-4 md:px-8">
        <Reveal className="text-center mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff4500] mb-4">{t.form.kicker}</p>
          <h2 className="font-clash font-black uppercase leading-[0.95] text-[#f5f5f5] text-4xl md:text-6xl tracking-tight">
            {t.form.heading}
          </h2>
          <p className="font-plex text-[#a3a3a3] mt-4 max-w-xl mx-auto">{t.form.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative bg-[#111] border border-white/10 p-6 md:p-12">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-16 text-center"
                  data-testid="register-success"
                >
                  <CheckCircle size={72} weight="fill" className="text-[#ff4500] mx-auto mb-6" />
                  <h3 className="font-clash font-bold text-3xl text-[#f5f5f5] mb-3">{t.form.successTitle}</h3>
                  <p className="font-plex text-[#a3a3a3] max-w-md mx-auto mb-8">{t.form.successBody}</p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="register-another"
                    className="font-mono text-xs uppercase tracking-wider border border-white/20 hover:border-[#ff4500] text-[#f5f5f5] px-6 py-3 transition-colors"
                  >
                    {t.form.another}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  className="space-y-6"
                  data-testid="register-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    {fieldOrder.map((name) => (
                      <div key={name}>
                        <label className="block font-mono text-[11px] font-medium text-[#a3a3a3] uppercase tracking-wider mb-2">
                          {t.form.fields[name]}
                        </label>
                        <input
                          type={fieldTypes[name]}
                          required
                          value={form[name]}
                          onChange={set(name)}
                          placeholder={t.form.placeholders[name]}
                          data-testid={`field-${name}`}
                          className={inputClass}
                        />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block font-mono text-[11px] font-medium text-[#a3a3a3] uppercase tracking-wider mb-2">
                      {t.form.volumeLabel}
                    </label>
                    <select
                      value={form.volume}
                      onChange={set("volume")}
                      data-testid="field-volume"
                      className={inputClass + " appearance-none cursor-pointer"}
                    >
                      {VOLUMES.map((v) => (
                        <option key={v} value={v} className="bg-[#111]">
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="register-submit"
                    className="group w-full flex items-center justify-center gap-3 bg-[#ff4500] hover:bg-[#e03c00] disabled:opacity-60 text-[#050505] font-semibold py-4 transition-colors"
                  >
                    {loading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" /> {t.form.sending}
                      </>
                    ) : (
                      <>
                        {t.form.submit}
                        <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
